import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './apiClient';

/**
 * Result shape from POST /api/audit/trigger — the manual audit trigger endpoint.
 */
export interface AuditTriggerResult {
  success: boolean;
  auditRunId: string;
  durationMs: number;
  summary: {
    totalDevices: number;
    coveragePercentage: number;
    criticalCount: number;
    clientCount: number;
    /** Issue ↔ device integrity of this run (absent from older API builds). */
    integrity?: RunIntegrity;
  };
  platforms: Array<{
    platform: string;
    success: boolean;
    deviceCount: number;
    error?: string;
  }>;
  triggeredBy: string;
}

/** Issue ↔ device-record integrity of one audit run. */
export interface RunIntegrity {
  /** Stored issues that contradict their device record after the run (must be 0). */
  issueInvariantViolations: number;
  violationSamples: Array<{ deviceName: string; issueType: string; reason: string }>;
  /** Issue rows from earlier runs removed by this run. */
  staleIssueRowsDeleted: number;
  staleIssueRowsByClient: Record<string, Record<string, number>>;
}

/**
 * Metadata wrapper for each config section — tracks who last modified it and when.
 * `value` is exactly what is stored server-side (no defaults merged in).
 */
export interface AuditConfigSection<T> {
  value: T;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Section keys accepted by PUT /api/settings/audit-config/{section}. */
export type AuditConfigSectionKey =
  | 'client-aliases'
  | 'huntress-only-clients'
  | 'required-platforms'
  | 'expected-discrepancies';

/**
 * A single expected discrepancy entry — suppresses audit noise for known-acceptable gaps.
 */
export interface ExpectedDiscrepancy {
  clientName: string;
  type: string;
  reason: string;
  expiresAt?: string;
}

/**
 * Full audit configuration object returned by GET /settings/audit-config.
 * Covers all four configurable sections.
 */
export interface AllAuditConfig {
  clientAliases: AuditConfigSection<Record<string, string>>;
  huntressOnlyClients: AuditConfigSection<string[]>;
  requiredPlatforms: AuditConfigSection<Record<string, string[]>>;
  expectedDiscrepancies: AuditConfigSection<ExpectedDiscrepancy[]>;
  /** Per-entry match counts from the latest audit run (null before the first run). */
  expectedDiscrepancyStatus?: {
    auditRunId: string;
    evaluatedAt: string;
    entries: Array<{ clientName: string; type: string; matchCount: number }>;
  } | null;
}

/** Maps a PUT section key to its property in AllAuditConfig. */
export const SECTION_PROPERTY: Record<
  AuditConfigSectionKey,
  'clientAliases' | 'huntressOnlyClients' | 'requiredPlatforms' | 'expectedDiscrepancies'
> = {
  'client-aliases': 'clientAliases',
  'huntress-only-clients': 'huntressOnlyClients',
  'required-platforms': 'requiredPlatforms',
  'expected-discrepancies': 'expectedDiscrepancies',
};

/** Response of PUT /api/settings/audit-config/{section}: the record as stored. */
export interface SaveAuditConfigResult<T = unknown> extends AuditConfigSection<T> {
  saved: true;
  section: AuditConfigSectionKey;
}

/**
 * Preview result showing which devices would be affected by a proposed config change.
 */
export interface PreviewResult {
  affectedDevices: Array<{
    deviceName: string;
    clientName: string;
    field: string;
    oldValue: string;
    newValue: string;
  }>;
  summary: {
    totalAffected: number;
    byClient: Record<string, number>;
  };
  /**
   * Expected-gap previews only: how many devices in the latest audit each proposed
   * entry matches. matchCount 0 = stale; null = type not evaluable from stored data.
   */
  expectedGapMatches?: Array<{
    clientName: string;
    type: string;
    matchCount: number | null;
    stale: boolean;
  }>;
}

/**
 * Fetches the complete audit configuration.
 * Cached for 30 seconds — frequent enough to pick up changes from other sessions.
 */
export function useAuditConfigQuery() {
  return useQuery({
    queryKey: ['settings', 'audit-config'],
    queryFn: () => apiFetch<AllAuditConfig>('/api/settings/audit-config'),
    staleTime: 30_000,
  });
}

/**
 * Mutation to persist a single config section by name (replaces the stored value).
 * On success the cache is updated from the server's stored record — never from what
 * was sent — and then refetched so every section re-syncs from the server.
 */
export function useSaveAuditConfigMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ section, value }: { section: AuditConfigSectionKey; value: unknown }) =>
      apiFetch<SaveAuditConfigResult>(`/api/settings/audit-config/${section}`, {
        method: 'PUT',
        body: { value },
        mutation: true,
        timeoutMs: 30_000,
      }),
    onSuccess: (result) => {
      const property = SECTION_PROPERTY[result.section];
      queryClient.setQueryData<AllAuditConfig>(['settings', 'audit-config'], (previous) =>
        previous
          ? {
              ...previous,
              [property]: {
                value: result.value,
                updatedAt: result.updatedAt,
                updatedBy: result.updatedBy,
              },
            }
          : previous
      );
      void queryClient.invalidateQueries({ queryKey: ['settings', 'audit-config'] });
    },
  });
}

/**
 * Mutation to preview the impact of a proposed config change before applying it.
 * Returns the set of devices that would be affected and a client-level summary.
 */
export function usePreviewAuditConfigMutation() {
  return useMutation({
    mutationFn: ({ section, proposedValue }: { section: string; proposedValue: unknown }) =>
      apiFetch<PreviewResult>('/api/settings/audit-config/preview', {
        method: 'POST',
        mutation: true, // in-dialog: token errors must show, never redirect or hang
        body: { section, proposedValue },
        timeoutMs: 20_000,
      }),
    // Retry transient failures only — a 4xx (e.g. a validation error) won't change on retry
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status < 500) && failureCount < 5,
    retryDelay: (attempt) => Math.min(3000 * 2 ** attempt, 30000),
  });
}

/**
 * Mutation to trigger a manual audit run.
 * Invalidates audit data caches on success so dashboards reflect new results.
 * 120s timeout — audits typically complete in 30-90 seconds.
 */
export function useTriggerAuditMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<AuditTriggerResult>('/api/audit/trigger', {
        method: 'POST',
        timeoutMs: 120_000,
        mutation: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      // Stale expected-gap badges come from the latest run
      void queryClient.invalidateQueries({ queryKey: ['settings', 'audit-config'] });
    },
    // A failed manual run is recorded server-side; refresh the failure banner either way
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit', 'run-status'] });
    },
  });
}
